import { Link } from "react-router-dom";
import { Star } from "lucide-react";
import { useFbFavs } from "@/lib/useFbFavs";

export const TeamLogo = ({ team, size = "w-5 h-5" }) => team?.logoUrl
  ? <img src={team.logoUrl} alt="" className={`${size} object-contain shrink-0`} loading="lazy" />
  : <span className={`${size} rounded-full bg-[#0A0C10] border border-[#273042] shrink-0`} />;

const toDate = (d) => new Date(d.replace(" ", "T") + (d.includes("Z") || d.includes("+") ? "" : "Z"));
export const fmtTime = (d) => (d ? toDate(d).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "");
const fmtDay = (d) => (d ? toDate(d).toLocaleDateString([], { day: "numeric", month: "short" }) : "TBD");

export const FavStar = ({ kind, item, testid, className = "" }) => {
  const { isFav, toggle } = useFbFavs();
  const on = isFav(kind, item.id);
  return (
    <button type="button" aria-label={on ? "Remove from favourites" : "Add to favourites"} data-testid={testid} data-active={on ? "true" : "false"}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(kind, item); }}
      className={`shrink-0 w-9 h-9 rounded-full inline-flex items-center justify-center transition-colors hover:bg-white/5 ${className}`}>
      <Star className={`w-[18px] h-[18px] transition-transform active:scale-90 ${on ? "fill-amber-400 text-amber-400" : "text-slate-500"}`} />
    </button>
  );
};

const Status = ({ f, showDate }) => {
  if (f.status === "live") return <span className="text-[#EF4444] font-bold text-xs leading-tight">LIVE<span className="block text-[10px] font-semibold opacity-80 truncate">{f.statusLabel}</span></span>;
  if (f.status === "finished") return <span className="text-slate-400 font-bold text-xs">{showDate ? fmtDay(f.date) : "FT"}{showDate && <span className="block text-[10px]">FT</span>}</span>;
  if (f.status === "off") return <span className="text-amber-400 font-bold text-[10px] uppercase leading-tight">{f.statusLabel}</span>;
  return <span className="text-white font-bold text-sm tabular-nums">{showDate && <span className="block text-[10px] text-slate-400 font-semibold">{fmtDay(f.date)}</span>}{fmtTime(f.date)}</span>;
};

// One fixture row (time · teams stacked · score · favourite) — opens the full match centre.
export const MatchRow = ({ f, showDate = false, showLeague = false }) => {
  const played = f.status === "finished" || f.status === "live";
  const live = f.status === "live";
  const dim = (mine, other) => (f.status === "finished" && (mine ?? 0) < (other ?? 0) ? "text-slate-400" : "text-white");
  return (
    <Link to={f.href || `/football/match/${f.id}`} state={f.href ? { match: f } : undefined} data-testid={`match-row-${f.id}`}
      className={`group rounded-xl bg-[#121620] border p-3 flex items-center gap-3 transition-colors hover:border-[#E11D2A]/60 ${live ? "border-[#E11D2A]/40" : "border-[#273042]"}`}>
      <span className="w-14 shrink-0 text-center"><Status f={f} showDate={showDate} /></span>
      <span className="w-px self-stretch bg-[#273042]" />
      <span className="flex-1 min-w-0 space-y-1.5">
        {[["home", f.home, f.homeScore, f.awayScore], ["away", f.away, f.awayScore, f.homeScore]].map(([k, t, mine, other]) => (
          <span key={k} className="flex items-center gap-2.5">
            <TeamLogo team={t} />
            <span className={`truncate text-sm sm:text-[15px] font-medium ${dim(mine, other)}`}>{t?.name}</span>
            {played && <span className={`ml-auto pl-2 tabular-nums font-extrabold ${live ? "text-[#EF4444]" : "text-white"}`} data-testid={`match-score-${k}-${f.id}`}>{mine ?? 0}</span>}
          </span>
        ))}
        {showLeague && f.league && <span className="block text-[10px] text-slate-500 truncate">{f.league}</span>}
      </span>
      <FavStar kind="matches" testid={`fav-match-${f.id}`}
        item={{ id: f.id, label: `${f.home?.name} v ${f.away?.name}`, date: f.date, name: f.league }} />
    </Link>
  );
};
